import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { HotBadge, LivePulse, SocialProof, LiveNudge, hotFor } from "@/components/Engagement";
import { toast } from "sonner";

const OUTCOMES = [
  { key: "get", title: "Get Clients", outcome: "Turn strangers into leads and SIPs", icon: "Magnet", color: "#FF8A00",
    tools: [{ label: "Marketing Factory", to: "/app/marketing", desc: "Goal-driven campaigns" }, { label: "My Microsite & Booking", to: "/app/website", desc: "Your digital storefront" }, { label: "Find a Consultant", to: "/app/consultants", desc: "Get expert help" }] },
  { key: "engage", title: "Engage Clients", outcome: "Know who needs you — and reach out", icon: "HeartHandshake", color: "#10B981",
    tools: [{ label: "Client 360", to: "/app/clients", desc: "Every client, one view" }, { label: "Retention Radar", to: "/app/retention", desc: "Save at-risk relationships" }, { label: "Appointments", to: "/app/appointments", desc: "Let clients book you" }] },
  { key: "content", title: "Create Content", outcome: "WhatsApp, posts & reels in one click", icon: "Wand2", color: "#A855F7",
    tools: [{ label: "Content Studio", to: "/app/content", desc: "AI content for every channel" }, { label: "Today's Content", to: "/app", desc: "Ready-to-send daily pack" }, { label: "Social Hub", to: "/app/social", desc: "Publish everywhere" }] },
  { key: "follow", title: "Follow Up", outcome: "Never miss the next conversation", icon: "PhoneCall", color: "#3B82F6",
    tools: [{ label: "Leads", to: "/app/leads", desc: "Pipeline & scores" }, { label: "Tasks", to: "/app/tasks", desc: "Your action list" }, { label: "Weekly Brief", to: "/app/weekly", desc: "This week's priorities" }] },
  { key: "trust", title: "Build Trust", outcome: "Stay compliant and credible", icon: "ShieldCheck", color: "#06B6D4",
    tools: [{ label: "Regulatory Hub", to: "/app/regulatory", desc: "SEBI & AMFI decoded" }, { label: "Fund Research", to: "/app/funds", desc: "Compare & recommend" }, { label: "Market Intelligence", to: "/app/market", desc: "Daily talking points" }] },
  { key: "grow", title: "Save Time & Grow", outcome: "See what's happening — and act", icon: "TrendingUp", color: "#F59E0B",
    tools: [{ label: "My Business", to: "/app/business", desc: "What changed & why" }, { label: "MFD Academy", to: "/app/learn", desc: "7-minute skills" }, { label: "MFD Community", to: "/app/community", desc: "Learn from peers" }] },
];

const OUT_CATS = {
  All: null,
  "Get Clients": ["Design"],
  "Create Content": ["Content", "Video", "Design"],
  "Engage & Follow Up": ["Voice", "Calling", "WhatsApp", "Email"],
  "Build Trust": ["Research", "Consultant"],
};
const ICONS = { Video: "Video", Voice: "AudioLines", Calling: "PhoneCall", Design: "Palette", Email: "Mail", Research: "Search", WhatsApp: "MessageCircle", Consultant: "UserCheck", Content: "FileText" };

export default function Tools() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [pricing, setPricing] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => { api.get("/wallet").then((r) => setPricing(r.data.pricing)).catch(() => {}); }, []);
  const allow = OUT_CATS[cat];
  const filtered = allow ? pricing.filter((p) => allow.includes(p.cat)) : pricing;

  const use = async (p) => {
    try { await api.post("/wallet/topup", { amount: -p.price }); await refreshUser(); toast.success(`${p.name} used · ₹${p.price} deducted`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Tools — by what you want to achieve" icon="LayoutGrid" color="#FF8A00"
        subtitle="Pick a business outcome. MFDx handles the how."
        action={<span className="bg-emerald-500/12 text-emerald-400 border border-emerald-500/20 rounded-full px-3 py-1.5 text-sm font-semibold">Wallet ₹{(user?.wallet ?? 0).toLocaleString("en-IN")}</span>} />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-6" data-testid="tools-live-strip">
        <LivePulse label="23 MFDs using tools now" />
        <SocialProof icon="Zap">8,900+ AI actions run this month</SocialProof>
        <SocialProof icon="Flame">Content Studio is trending today</SocialProof>
      </div>

      {/* Outcome goals */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="outcome-goals">
        {OUTCOMES.map((o) => (
          <div key={o.key} className="card-mfx p-5" data-testid={`outcome-${o.key}`}>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${o.color}1f` }}><Icon name={o.icon} size={22} style={{ color: o.color }} /></span>
              <div><h3 className="font-head font-semibold text-slate-100 leading-tight">{o.title}</h3><p className="text-xs text-slate-500">{o.outcome}</p></div>
            </div>
            <div className="space-y-1.5">
              {o.tools.map((t) => (
                <button key={t.label} onClick={() => nav(t.to)} data-testid={`outcome-tool-${t.label.replace(/\s/g, "-").toLowerCase()}`}
                  className="w-full text-left flex items-center gap-2 rounded-xl border border-[#222938] hover:border-amber-500/40 px-3 py-2 transition-colors">
                  <div className="min-w-0"><div className="text-sm font-medium text-slate-200 truncate">{t.label}</div><div className="text-[11px] text-slate-500 truncate">{t.desc}</div></div>
                  <Icon name="ArrowRight" size={15} className="ml-auto text-slate-600 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Pay-as-you-use AI tools */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl grid place-items-center bg-cyan-500/12"><Icon name="Zap" size={18} className="text-cyan-400" /></div>
        <div><h2 className="font-head font-semibold text-lg text-slate-100 leading-tight">Pay-as-you-use AI tools</h2><p className="text-xs text-slate-500">One wallet, every AI tool — no 20 subscriptions</p></div>
      </div>
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {Object.keys(OUT_CATS).map((c) => <button key={c} onClick={() => setCat(c)} data-testid={`toolcat-${c.replace(/\s/g, "-").toLowerCase()}`} className={`px-3.5 py-1.5 rounded-full text-sm font-medium border whitespace-nowrap ${cat === c ? "grad-amber text-black border-transparent" : "bg-[#141822] border-[#222938] text-slate-400 hover:text-slate-200"}`}>{c}</button>)}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((p) => (
          <div key={p.key} className="card-mfx overflow-hidden hover-lift" data-testid="tool-card">
            <div className="h-16 grad-amber grid place-items-center"><Icon name={ICONS[p.cat] || "Sparkles"} size={26} className="text-black/80" /></div>
            <div className="p-5">
              <span className="text-[11px] font-semibold bg-gray-100 rounded-full px-2 py-0.5 text-gray-500">{p.cat}</span>
              <h3 className="font-head font-semibold text-lg mt-2 text-slate-100">{p.name}</h3>
              <div className="font-head font-bold text-xl text-amber-400 mt-1">₹{p.price}<span className="text-xs text-slate-500 font-normal">/{p.unit}</span></div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <HotBadge badge={hotFor(p.key).badge} />
                <SocialProof icon="Zap">Used {hotFor(p.key).booked}× today</SocialProof>
              </div>
              <Btn onClick={() => use(p)} className="mt-4 w-full justify-center" data-testid="tool-use-btn"><Icon name="Zap" size={15} /> Use now</Btn>
            </div>
          </div>
        ))}
      </div>

      <Card className="mt-6">
        <div className="flex items-center gap-2 mb-2"><Icon name="PhoneCall" size={18} style={{ color: "#A855F7" }} /><h3 className="font-head font-semibold text-slate-100">AI Calling — responsible use</h3></div>
        <p className="text-sm text-gray-500">Calling campaigns require explicit consent and respect DND / telemarketing regulations and opt-out. Use cases: lead follow-up, appointment & webinar reminders, feedback. Never unrestricted cold-calling.</p>
      </Card>

      <LiveNudge id="tools" title="Turn a goal into action" message="Pick a business outcome and let MFDx handle the how — most MFDs start with content." ctaLabel="Got it" />
    </div>
  );
}
